import React from 'react';
import { useSelector } from 'react-redux';

import { cartItemCountSelector } from '../../ducks/cart.duck';
import { useIntl } from '../../util/reactIntl';

import NamedLink from '../NamedLink/NamedLink';

import css from './CartIcon.module.css';

/**
 * Badge text for the cart. Hidden when the cart is empty; capped at 99+.
 *
 * @param {number|null|undefined} count total units (sum of qty)
 * @returns {string|null}
 */
export const formatCartBadge = count => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) {
    return null;
  }
  return n > 99 ? '99+' : String(Math.floor(n));
};

/**
 * Accessible name for the cart control. The badge is decorative, so the count is
 * only ever announced from here.
 *
 * @param {Object} intl
 * @param {function} intl.formatMessage
 * @param {number|null|undefined} itemCount total units
 * @returns {string}
 */
export const cartAriaLabel = (intl, itemCount) => {
  const n = Number(itemCount);
  return Number.isFinite(n) && n > 0
    ? intl.formatMessage({ id: 'CartIcon.itemsInCart' }, { count: n })
    : intl.formatMessage({ id: 'CartIcon.cart' });
};

/** Shopping bag glyph. Inherits the topbar colour through currentColor. */
const CartIconGlyph = ({ size = 24 }) => (
  <span className={css.glyph} style={{ width: size, height: size }} aria-hidden="true" />
);

/**
 * Topbar cart control with a unit-count badge. Rendered for guests too — a guest cart
 * is a real cart.
 *
 * Cart hydration and the login merge deliberately live in CartLifecycle, which is
 * mounted once per page. This component renders in both the mobile and the desktop
 * topbar, so any effect here would run twice.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className]
 * @param {string} [props.iconWrapClassName]
 * @param {string} [props.badgeClassName]
 * @param {number} [props.iconSize]
 * @returns {JSX.Element}
 */
const CartIcon = props => {
  const { className, iconWrapClassName, badgeClassName, iconSize = 24 } = props;
  const intl = useIntl();
  const itemCount = useSelector(cartItemCountSelector);

  const badge = formatCartBadge(itemCount);
  const label = cartAriaLabel(intl, itemCount);

  return (
    <NamedLink name="CartPage" className={className} title={label} ariaLabel={label}>
      <span className={iconWrapClassName || css.iconWrap}>
        <CartIconGlyph size={iconSize} />
        {badge ? (
          <span className={badgeClassName || css.badge} aria-hidden="true">
            {badge}
          </span>
        ) : null}
      </span>
    </NamedLink>
  );
};

export default CartIcon;
